import { CoverTemplate, CoverTemplateValues } from '../types';
import { getDefaultCoverValues, buildDocumentFromCoverSections } from './templateUtils';
import { DocumentSection, TextBlock, ImageBlock, TableBlock } from '../../core/ast/document.types';

export const labReportCoverTemplate: CoverTemplate = {
  id: 'tpl-lab-report-cover',
  name: 'EWU Group Lab Report (Sample Format)',
  category: 'lab_report',
  description: 'East West University laboratory experiment format with flexible display options for Group Members only, Submitted By only, or both.',
  thumbnailBadge: 'EWU Group Lab',
  version: 3,
  supportedFields: [
    'universityName', 'universityAddress', 'departmentName', 'logoAssetId', 'showLogo',
    'documentTypeLabel', 'title', 'subtitle', 'experimentNumber', 'experimentName', 'groupNumber',
    'courseCode', 'courseTitle', 'sectionOrGroup', 'groupMembers',
    'studentName', 'studentId', 'facultyName', 'facultyDesignation', 'facultyDepartment',
    'dateOfPerformance', 'submissionDate', 'semester', 'academicYear', 'submissionDisplayMode',
  ],
  defaultValues: getDefaultCoverValues({
    documentTypeLabel: 'Lab Report',
    title: 'Lab Report',
    experimentNumber: '',
    experimentName: '',
    groupNumber: '',
    courseCode: '',
    courseTitle: '',
    sectionOrGroup: '',
    semester: '',
    studentName: '',
    studentId: '',
    groupMembers: '',
    facultyName: '',
    facultyDesignation: '',
    facultyDepartment: '',
    dateOfPerformance: '',
    submissionDate: '',
    submissionDisplayMode: 'both',
    borderStyle: 'none',
  }),
  compileToDocument: (values: CoverTemplateValues, pageSettings) => {
    const now = new Date().toISOString();
    const blocks: Array<TextBlock | ImageBlock | TableBlock> = [];

    const displayMode = values.submissionDisplayMode || 'both';
    const showGroup = displayMode === 'both' || displayMode === 'group_only';
    const showSubmittedBy = displayMode === 'both' || displayMode === 'submitted_by_only';

    // 1. Center-Aligned Logo
    if (values.showLogo && values.logoAssetId) {
      blocks.push({
        id: 'blk-cover-logo',
        type: 'image',
        createdAt: now,
        data: {
          assetId: values.logoAssetId,
          previewDataUrl: values.logoAssetId,
          widthPercent: 26,
          alignment: 'center',
          altText: values.universityName || 'East West University',
        },
      });
    }

    // 2. University Name (Center Aligned)
    blocks.push({
      id: 'blk-cover-univ',
      type: 'text',
      createdAt: now,
      data: {
        text: (values.universityName || 'EAST WEST UNIVERSITY').toUpperCase(),
        formatting: { bold: true, fontSizePt: 16, alignment: 'center', color: '#1a365d' },
      },
    });

    // 3. University Address (Center Aligned)
    blocks.push({
      id: 'blk-cover-addr',
      type: 'text',
      createdAt: now,
      data: {
        text: values.universityAddress || 'A/2 Jahurul Islam Ave, Dhaka 1212',
        formatting: { fontSizePt: 10, alignment: 'center', color: '#4a5563' },
      },
    });

    // Space before Title
    blocks.push({
      id: 'blk-cover-sp1',
      type: 'text',
      createdAt: now,
      data: {
        text: '\n',
        formatting: { fontSizePt: 10 },
      },
    });

    // 4. Lab Report Title & Header Details
    blocks.push({
      id: 'blk-cover-title',
      type: 'text',
      createdAt: now,
      data: {
        text: values.documentTypeLabel || 'Lab Report',
        formatting: { bold: true, fontSizePt: 18, alignment: 'center', color: '#111827' },
      },
    });

    const semesterLine = values.semester ? `Semester: ${values.semester}` : '';
    const courseCodeStr = values.courseCode ? `Course Code: ${values.courseCode}` : '';
    const courseTitleStr = values.courseTitle ? `Course Title: ${values.courseTitle}` : '';
    const courseLine = [courseTitleStr, courseCodeStr].filter(Boolean).join('       ');
    const sectionLine = values.sectionOrGroup ? `Section: ${values.sectionOrGroup}` : '';

    const headerLines = [semesterLine, courseLine, sectionLine].filter(Boolean).join('\n');
    if (headerLines) {
      blocks.push({
        id: 'blk-cover-course-header',
        type: 'text',
        createdAt: now,
        data: {
          text: `${headerLines}\n`,
          formatting: { bold: true, fontSizePt: 11, alignment: 'center', color: '#1f2937' },
        },
      });
    }

    // 5. Experiment & Group Information (Left Aligned)
    const exptNo = values.experimentNumber ? `Expt No: ${values.experimentNumber}` : '';
    const exptName = values.experimentName || values.title ? `Expt Name: ${values.experimentName || values.title}` : '';
    const groupNo = values.groupNumber ? `Group No: ${values.groupNumber}` : '';

    const exptInfoLines = [exptNo, exptName, groupNo].filter(Boolean).join('\n');
    if (exptInfoLines) {
      blocks.push({
        id: 'blk-cover-expt-info',
        type: 'text',
        createdAt: now,
        data: {
          text: `${exptInfoLines}${showGroup ? '\nGroup Members:' : ''}`,
          formatting: { bold: true, fontSizePt: 11, alignment: 'left', color: '#111827' },
        },
      });
    }

    // 6. Group Members Table (If Group display enabled)
    if (showGroup) {
      const rawMembers = values.groupMembers || '';
      const memberRows: string[][] = rawMembers
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .map((line) => {
          if (line.includes('\t')) {
            const parts = line.split('\t');
            return [parts[0].trim(), parts.slice(1).join(' ').trim()];
          }
          const match = line.match(/^(.*?)(?:\s+)(\d{4}-\d-\d{2}-\d{3})$/);
          if (match) {
            return [match[1].trim(), match[2].trim()];
          }
          if (line.includes(':')) {
            const parts = line.split(':');
            return [parts[0].trim(), parts.slice(1).join(':').trim()];
          }
          return [line, ''];
        });

      if (memberRows.length > 0) {
        const tableCells = [
          [
            { id: 'c-h1', content: 'Student Name', isHeader: true, bold: true, alignment: 'center' as const },
            { id: 'c-h2', content: 'Student Id', isHeader: true, bold: true, alignment: 'center' as const },
          ],
          ...memberRows.map((row, rIdx) => [
            { id: `c-${rIdx}-1`, content: row[0], alignment: 'center' as const },
            { id: `c-${rIdx}-2`, content: row[1], alignment: 'center' as const },
          ]),
        ];

        blocks.push({
          id: 'blk-cover-group-table',
          type: 'table',
          createdAt: now,
          data: {
            rows: tableCells.length,
            cols: 2,
            hasHeaderRow: true,
            hasHeaderCol: false,
            borderStyle: 'academic',
            cells: tableCells,
          },
        });
      }
    }

    // Spacing
    blocks.push({
      id: 'blk-cover-sp2',
      type: 'text',
      createdAt: now,
      data: {
        text: '\n',
        formatting: { fontSizePt: 10 },
      },
    });

    // 7. Submitted By (If Submitted By display enabled)
    if (showSubmittedBy && (values.studentName || values.studentId)) {
      blocks.push({
        id: 'blk-cover-subby',
        type: 'text',
        createdAt: now,
        data: {
          text: `Submitted by-\nName: ${values.studentName || ''}\nId: ${values.studentId || ''}\n`,
          formatting: { fontSizePt: 11, alignment: 'center', color: '#111827' },
        },
      });
    }

    // 8. Submitted To (Center Aligned)
    const faculty = values.facultyName ? values.facultyName : '';
    const designation = values.facultyDesignation || '';
    const dept = values.facultyDepartment || values.departmentName || '';

    if (faculty || designation || dept) {
      blocks.push({
        id: 'blk-cover-subto',
        type: 'text',
        createdAt: now,
        data: {
          text: `Submitted to-\n${faculty ? faculty + '\n' : ''}${designation ? designation + '\n' : ''}${dept ? dept + '\n' : ''}${values.universityName || 'East West University'}\n`,
          formatting: { fontSizePt: 11, alignment: 'center', color: '#111827' },
        },
      });
    }

    // 9. Dates of Performance and Submission
    const perfDate = values.dateOfPerformance ? `Date of Performance: ${values.dateOfPerformance}` : '';
    const subDate = values.submissionDate ? `Date of Submission: ${values.submissionDate}` : '';

    const dateLines = [perfDate, subDate].filter(Boolean).join('\n');
    if (dateLines) {
      blocks.push({
        id: 'blk-cover-dates',
        type: 'text',
        createdAt: now,
        data: {
          text: dateLines,
          formatting: { bold: true, fontSizePt: 11, alignment: 'left', color: '#111827' },
        },
      });
    }

    const coverSection: DocumentSection = {
      id: 'sec-cover-lab-sample',
      title: 'Cover Page',
      type: 'cover',
      enabled: true,
      collapsed: false,
      blocks,
    };

    return buildDocumentFromCoverSections(values, [coverSection], pageSettings);
  },
};
